import { ContainersTable } from "@/components/Layout/Containers.table";
import { useStore } from "@/stores/useStore";
import { Container } from "@/types/v2";
import { Button } from "@/components/Base/Button";
import { Link, useParams } from "react-router-dom";
import { container as containerRepository } from "@/repositories/container";
import { ContainerDialog } from "@/components/Layout/ContainerDialog";
import { PlusIcon } from "lucide-react";
import { useMemo } from "react";
import { getIntegrationName } from "@/lib/integration";
import { isTechnicianWritebackEnabledForShop } from "@/lib/technicianWriteback";
import { layout as layoutRepository } from "@/repositories/layout";
import { getLinkableLabels } from "@/lib/getLinkableLabels";

export const LayoutContainersPage = () => {
  const { shopId } = useParams();
  const { shop, employees, cards } = useStore();
  const containers = useMemo(() => shop?.containers ?? [], [shop]);
  const labels = useMemo(() => (shop ? getLinkableLabels(shop) : []), [shop]);
  const layouts = useMemo(() => shop?.layouts ?? [], [shop]);

  const handleCreate = async (container: Partial<Container>) => {
    if (!container.displayName) return;
    await containerRepository.createContainer(
      container.displayName,
      container.linkedTechnicianId,
      container.linkedSourceLabel,
      undefined,
      container.bidirectionalTechnicianId
    );
  };

  const handleEdit = async (container: Container) => {
    await containerRepository.updateContainer(container.id, container);
  };

  const handleDelete = async (container: Container) => {
    if (!shopId) return;
    await containerRepository.deleteContainer(container.id);
  };

  const handleAddToLayout = async (
    containerIds: string[],
    layoutName: string
  ) => {
    if (!shopId) return;
    const target = layouts.find((l) => Object.keys(l)[0] === layoutName);
    if (!target) return;

    const updatedLayoutData = layoutRepository.addContainersToLayout(
      containerIds,
      target[layoutName]
    );
    if (updatedLayoutData.length === target[layoutName].length) return;

    await layoutRepository.updateLayout(shopId, layoutName, updatedLayoutData);
  };

  return (
    <div className="overflow-auto p-4">
      <div className="flex flex-row items-center justify-between">
        <Button variant="secondary">
          <Link to={`/${shopId}/layoutBuilder`}>Layout Builder</Link>
        </Button>
        <ContainerDialog
          employees={employees}
          labels={labels}
          labelCatalog={shop?.labelCatalog}
          containers={containers}
          onSubmit={handleCreate}
          triggerIcon={<PlusIcon className={"mr-2"} />}
          triggerVariant={"accent"}
          integrationName={getIntegrationName(shop)}
          technicianWritebackEnabled={
            shop ? isTechnicianWritebackEnabledForShop(shop) : false
          }
        />
      </div>
      <ContainersTable
        containers={containers}
        employees={employees ?? []}
        layouts={layouts}
        labels={labels}
        labelCatalog={shop?.labelCatalog}
        cards={cards}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAddToLayout={handleAddToLayout}
        showEditLinks
        integrationName={getIntegrationName(shop)}
        technicianWritebackEnabled={
          shop ? isTechnicianWritebackEnabledForShop(shop) : false
        }
      />
    </div>
  );
};
